import { copyText } from "@/lib/uid.ts";
import type { ProjectApp } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, Globe, Link2, Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { previewApi, shareApi } from "@/api/browser.ts";
import { useAuthStatus } from "@/features/auth/session.ts";
import { useApiError } from "@/ui/useApiError.ts";
import { PublishPanel } from "./PublishPanel.tsx";

/** Dropdown under the globe button: ports inside the container, share links, public domains. */
export function AppsMenu({ projectId, canEdit, onOpenInternal, onClose }: { projectId: string; canEdit: boolean; onOpenInternal: (url: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const me = useAuthStatus().data?.user;
  const apps = useQuery({ queryKey: ["projects", projectId, "apps"], queryFn: () => previewApi.apps(projectId), refetchInterval: 5000 });
  const shares = useQuery({ queryKey: ["projects", projectId, "shares"], queryFn: () => shareApi.list(projectId) });
  const deployments = useQuery({ queryKey: ["projects", projectId, "deployments"], queryFn: () => shareApi.deployments(projectId) });
  const [fresh, setFresh] = useState<{ port: number; url: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  // the "stable public domain" panel for one port (publish, rename, history)
  const [panelPort, setPanelPort] = useState<number | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["projects", projectId, "shares"] });
    void qc.invalidateQueries({ queryKey: ["projects", projectId, "deployments"] });
  };
  const share = useMutation({ mutationFn: (port: number) => shareApi.create(projectId, port), onSuccess: (s) => { setFresh({ port: s.port, url: s.url! }); refresh(); } });
  const revoke = useMutation({ mutationFn: (id: string) => shareApi.revoke(projectId, id), onSuccess: () => { setFresh(null); refresh(); } });
  const unpublish = useMutation({ mutationFn: (sub: string) => shareApi.unpublish(projectId, sub), onSuccess: refresh });
  const copy = async (text: string, key: string) => {
    await copyText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };
  const openExternal = async (port: number) => {
    const w = window.open("about:blank", "_blank");
    try {
      const e = await previewApi.entry(projectId, port);
      if (w) w.location.href = e.url;
    } catch {
      w?.close();
    }
  };
  const err = share.error ?? revoke.error ?? unpublish.error;
  const shareFor = (port: number) => shares.data?.find((s) => s.port === port);
  const deployFor = (port: number) => deployments.data?.filter((d) => d.port === port) ?? [];

  return (
    <div className="absolute end-2 top-full z-20 mt-1 w-[min(20rem,calc(100%-1rem))] rounded-md border border-border bg-surface p-2 text-xs shadow-lg" dir="auto">
      <div className="mb-1 flex items-center justify-between px-1 text-fg-muted"><span>{t("browser.appsTitle")}</span>{apps.isFetching && <Loader2 className="size-3 animate-spin" />}</div>
      {apps.data?.length === 0 && <p className="px-1 py-2 text-fg-muted">{t("browser.noApps")}</p>}
      {err && <p className="px-1 py-1 text-danger">{toMsg(err)}</p>}
      <ul className="max-h-80 space-y-1 overflow-auto">
        {(apps.data ?? []).map((a: ProjectApp) => {
          const s = shareFor(a.port);
          const ds = deployFor(a.port);
          return (
            <li key={a.port} className="rounded border border-border p-1.5">
              <div className="flex items-center gap-1">
                <button type="button" className="min-w-0 flex-1 text-start" disabled={!canEdit} onClick={() => { onOpenInternal(a.internalUrl); onClose(); }}>
                  <span dir="ltr" className="font-mono">:{a.port}</span> <span className="truncate text-fg-muted">{a.process}</span>
                  {a.loopback && <span className="ms-1 rounded bg-warn-soft px-1 text-[10px]">{t("browser.loopback")}</span>}
                </button>
                <button type="button" className="rounded p-1 hover:bg-border" title={t("browser.openExternal")} onClick={() => void openExternal(a.port)}><ExternalLink className="size-3.5" /></button>
                {canEdit && <button type="button" className="rounded p-1 hover:bg-border" title={s ? t("browser.shareReplace") : t("browser.share")} onClick={() => share.mutate(a.port)}><Link2 className="size-3.5" /></button>}
                {me?.role === "admin" && <button type="button" className="rounded p-1 hover:bg-border" title={t("browser.publish")} onClick={() => setPanelPort(a.port)}><Globe className="size-3.5" /></button>}
              </div>
              {fresh?.port === a.port && (
                <div className="mt-1 flex items-center gap-1 rounded bg-success-soft px-1.5 py-1">
                  <span dir="ltr" className="min-w-0 flex-1 truncate font-mono">{fresh.url}</span>
                  <button type="button" className="rounded p-0.5" onClick={() => void copy(fresh.url, "share")}>{copied === "share" ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}</button>
                </div>
              )}
              {s && fresh?.port !== a.port && (
                <div className="mt-1 flex items-center justify-between text-fg-muted">
                  <span>{t("browser.shareActive")}</span>
                  {canEdit && <button type="button" className="rounded p-0.5 hover:text-danger" title={t("browser.revoke")} onClick={() => revoke.mutate(s.id)}><Trash2 className="size-3.5" /></button>}
                </div>
              )}
              {ds.map((d) => (
                <div key={d.subdomain} className="mt-1 flex items-center gap-1 text-fg-muted">
                  <Globe className="size-3 shrink-0" />
                  <a dir="ltr" href={d.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate font-mono text-accent">{d.url}</a>
                  <button type="button" className="rounded p-0.5" onClick={() => void copy(d.url, d.subdomain)}>{copied === d.subdomain ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}</button>
                  {me?.role === "admin" && <button type="button" className="rounded p-0.5 hover:text-danger" onClick={() => unpublish.mutate(d.subdomain)}><Trash2 className="size-3.5" /></button>}
                </div>
              ))}
            </li>
          );
        })}
      </ul>
      <p className="mt-1 border-t border-border px-1 pt-1 text-[11px] text-fg-muted">{t("browser.appsHint")}</p>
      {panelPort !== null && <PublishPanel projectId={projectId} port={panelPort} onClose={() => setPanelPort(null)} />}
    </div>
  );
}
